'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Globe, GraduationCap, DollarSign, Sparkles, Send, Bot, User, ArrowRight, Award } from 'lucide-react';

const destinations = [
  { country: 'United States', flag: '🇺🇸', universities: 4500, avgTuition: '$35,000', topUni: 'MIT, Harvard, Stanford', color: 'from-blue-600 to-blue-800', students: '2.5L+' },
  { country: 'United Kingdom', flag: '🇬🇧', universities: 160, avgTuition: '£18,000', topUni: 'Oxford, Cambridge, Imperial', color: 'from-red-600 to-red-800', students: '1.2L+' },
  { country: 'Canada', flag: '🇨🇦', universities: 220, avgTuition: 'CAD $25,000', topUni: 'UofT, UBC, McGill', color: 'from-rose-600 to-rose-800', students: '90K+' },
  { country: 'Australia', flag: '🇦🇺', universities: 43, avgTuition: 'AUD $30,000', topUni: 'ANU, Melbourne, Sydney', color: 'from-yellow-600 to-yellow-800', students: '75K+' },
  { country: 'Germany', flag: '🇩🇪', universities: 380, avgTuition: '€500 (Public)', topUni: 'TU Munich, LMU, Heidelberg', color: 'from-gray-600 to-gray-800', students: '45K+' },
  { country: 'Singapore', flag: '🇸🇬', universities: 6, avgTuition: 'SGD $20,000', topUni: 'NUS, NTU, SMU', color: 'from-red-500 to-red-700', students: '20K+' },
];

const programs = [
  { name: 'MS Computer Science', duration: '2 years', intake: 'Fall / Spring', exam: 'GRE + TOEFL', avg_salary: '$95K', icon: '💻' },
  { name: 'MBA', duration: '2 years', intake: 'Fall', exam: 'GMAT + TOEFL', avg_salary: '$110K', icon: '📊' },
  { name: 'MS Data Science', duration: '1.5 years', intake: 'Fall / Spring', exam: 'GRE + IELTS', avg_salary: '$88K', icon: '📈' },
  { name: 'MS Electrical Eng.', duration: '2 years', intake: 'Fall', exam: 'GRE + TOEFL', avg_salary: '$92K', icon: '⚡' },
  { name: 'MS Mechanical Eng.', duration: '2 years', intake: 'Fall / Spring', exam: 'GRE + TOEFL', avg_salary: '$85K', icon: '⚙️' },
  { name: 'LLM (Law)', duration: '1 year', intake: 'Fall', exam: 'LSAT + TOEFL', avg_salary: '$120K', icon: '⚖️' },
];

interface ChatMessage {
  role: 'user' | 'ai';
  text: string;
}

const aiResponses: Record<string, string> = {
  default: "I can help you plan your study abroad journey! Ask me about universities, scholarships, visa requirements, or the best countries for your field of study.",
  usa: "The USA has 4,500+ universities. For MS programs, top choices include MIT, Stanford, CMU, and UC Berkeley. Average tuition is $25,000–$55,000/year. You'll need GRE (310+) and TOEFL (100+) for top schools. Scholarships like Fulbright are available for Indian students.",
  uk: "The UK offers 1-year MS programs, saving you time and money. Top universities: Oxford, Cambridge, Imperial, UCL. Tuition: £15,000–£35,000/year. Post-Study Work Visa allows 2 years of work after graduation. IELTS 6.5+ is typically required.",
  scholarship: "Top scholarships for Indian students: 1) Fulbright-Nehru (USA) — full funding, 2) Chevening (UK) — full funding, 3) DAAD (Germany) — €850/month stipend, 4) Erasmus Mundus (Europe) — €1,000/month, 5) Commonwealth Scholarship — full funding for developing nations.",
  visa: "For a US F-1 Student Visa: 1) Get I-20 from university, 2) Pay SEVIS fee ($350), 3) Fill DS-160 form, 4) Book visa appointment, 5) Attend interview with financial documents. Processing takes 2–8 weeks. Apply 3 months before your program starts.",
  gre: "GRE prep tips: 1) Target 320+ for top US universities, 2) Verbal: 155+, Quant: 165+ for STEM, 3) Use Manhattan Prep or Magoosh for 3-month prep, 4) Take 5+ mock tests, 5) GRE is valid for 5 years. Many universities are now GRE-optional post-2024.",
};

function getAIResponse(input: string): string {
  const lower = input.toLowerCase();
  if (lower.includes('usa') || lower.includes('america') || lower.includes('united states')) return aiResponses.usa;
  if (lower.includes('uk') || lower.includes('britain') || lower.includes('england')) return aiResponses.uk;
  if (lower.includes('scholarship') || lower.includes('funding') || lower.includes('financial')) return aiResponses.scholarship;
  if (lower.includes('visa') || lower.includes('f-1') || lower.includes('student visa')) return aiResponses.visa;
  if (lower.includes('gre') || lower.includes('exam') || lower.includes('test')) return aiResponses.gre;
  return aiResponses.default;
}

export default function StudyAbroadPage() {
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: 'ai', text: "Hi! I'm your AI Study Abroad Counsellor 🌍 Ask me anything about universities, scholarships, visa, or programs abroad!" }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const sendMessage = () => {
    if (!input.trim()) return;
    const userMsg: ChatMessage = { role: 'user', text: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);
    setTimeout(() => {
      const aiMsg: ChatMessage = { role: 'ai', text: getAIResponse(input) };
      setMessages(prev => [...prev, aiMsg]);
      setIsTyping(false);
    }, 1200);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">

        {/* Hero */}
        <section className="relative bg-secondary overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-primary blur-3xl -translate-y-1/2 translate-x-1/4" />
            <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-violet-400 blur-3xl translate-y-1/2 -translate-x-1/4" />
          </div>
          <div className="relative max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-16 lg:py-20">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-sm text-slate-200 mb-5 border border-white/20">
                <Globe size={14} className="text-primary" />
                <span>Study in 50+ Countries — 10,000+ Programs</span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-4">
                Your Global Education<br />
                <span className="text-primary">Starts Here</span>
              </h1>
              <p className="text-slate-300 text-lg mb-8 max-w-xl">
                Explore top universities worldwide, get AI-powered guidance, and apply to your dream program — all in one place.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link href="#destinations" className="btn-primary px-6 py-3">
                  Explore Destinations
                </Link>
                <Link href="#ai-counsellor" className="btn-secondary border-white/30 text-white hover:bg-white/10 px-6 py-3">
                  Talk to AI Counsellor
                </Link>
              </div>
              <div className="flex flex-wrap gap-6 mt-8 text-sm text-slate-300">
                {[['50+', 'Countries'], ['10,000+', 'Programs'], ['500+', 'Scholarships'], ['2L+', 'Students Placed']].map(([num, label]) => (
                  <div key={label} className="flex items-center gap-1.5">
                    <span className="font-bold text-white">{num}</span> {label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Destinations */}
        <section id="destinations" className="py-14 px-4 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-foreground">Top Study Destinations</h2>
            <p className="text-muted-foreground text-sm mt-1">Choose from the world's most sought-after education hubs</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {destinations.map((dest) => (
              <div key={dest.country} className="group bg-card rounded-2xl border border-border overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-pointer">
                <div className={`bg-gradient-to-br ${dest.color} p-6 relative overflow-hidden`}>
                  <div className="absolute top-0 right-0 text-8xl opacity-20 -translate-y-2 translate-x-2">{dest.flag}</div>
                  <span className="text-4xl">{dest.flag}</span>
                  <h3 className="text-white font-extrabold text-xl mt-2">{dest.country}</h3>
                  <p className="text-white/70 text-sm">{dest.students} Indian students</p>
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <GraduationCap size={14} className="text-primary shrink-0" />
                    <span>{dest.universities} universities</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <DollarSign size={14} className="text-primary shrink-0" />
                    <span>Avg. {dest.avgTuition}/year</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Award size={14} className="text-primary shrink-0" />
                    <span className="truncate">{dest.topUni}</span>
                  </div>
                  <button className="w-full mt-3 btn-secondary text-sm py-2 group-hover:bg-primary group-hover:text-white transition-colors">
                    Explore Programs <ArrowRight size={14} className="inline ml-1" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Programs */}
        <section className="py-14 bg-muted/50">
          <div className="px-4 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-foreground">Popular Programs Abroad</h2>
              <p className="text-muted-foreground text-sm mt-1">Most applied programs by Indian students in 2025–26</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {programs.map((prog) => (
                <div key={prog.name} className="bg-card rounded-2xl border border-border p-5 hover:border-primary hover:shadow-md transition-all duration-200">
                  <div className="flex items-start gap-3 mb-4">
                    <span className="text-3xl">{prog.icon}</span>
                    <div>
                      <h3 className="font-bold text-foreground">{prog.name}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">Avg. Salary: <span className="text-primary font-bold">{prog.avg_salary}/yr</span></p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="bg-muted rounded-lg py-2">
                      <p className="font-bold text-foreground">{prog.duration}</p>
                      <p className="text-muted-foreground">Duration</p>
                    </div>
                    <div className="bg-muted rounded-lg py-2">
                      <p className="font-bold text-foreground text-xs">{prog.intake}</p>
                      <p className="text-muted-foreground">Intake</p>
                    </div>
                    <div className="bg-muted rounded-lg py-2">
                      <p className="font-bold text-foreground text-xs">{prog.exam}</p>
                      <p className="text-muted-foreground">Exam</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* AI Counsellor */}
        <section id="ai-counsellor" className="py-14 px-4 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-4 py-1.5 text-sm text-primary mb-5">
                <Sparkles size={14} />
                <span>AI-Powered Guidance</span>
              </div>
              <h2 className="text-3xl font-extrabold text-foreground mb-4">
                Your Personal AI<br />Study Abroad Counsellor
              </h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Get instant answers about universities, scholarships, visa requirements, and more. Our AI counsellor is trained on data from 50,000+ successful applications.
              </p>
              <div className="space-y-3">
                {['Which country is best for MS in CS?', 'What scholarships are available for Indian students?', 'How do I get a US student visa?', 'What GRE score do I need for MIT?'].map((q) => (
                  <button
                    key={q}
                    onClick={() => setInput(q)}
                    className="w-full text-left px-4 py-3 rounded-xl border border-border bg-card hover:border-primary hover:bg-accent text-sm font-semibold text-foreground transition-all duration-150"
                  >
                    💬 {q}
                  </button>
                ))}
              </div>
            </div>

            {/* Chat UI */}
            <div className="bg-card rounded-2xl border border-border overflow-hidden shadow-card">
              <div className="bg-secondary px-5 py-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
                  <Bot size={18} className="text-white" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm">All About Campus AI Counsellor</p>
                  <p className="text-slate-400 text-xs flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
                    Online — Simulated AI
                  </p>
                </div>
              </div>
              <div className="h-72 overflow-y-auto p-4 space-y-3 scrollbar-thin">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${msg.role === 'ai' ? 'bg-primary' : 'bg-secondary'}`}>
                      {msg.role === 'ai' ? <Bot size={14} className="text-white" /> : <User size={14} className="text-white" />}
                    </div>
                    <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${msg.role === 'ai' ? 'bg-muted text-foreground rounded-tl-none' : 'bg-primary text-white rounded-tr-none'}`}>
                      {msg.text}
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div className="flex gap-2">
                    <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center shrink-0">
                      <Bot size={14} className="text-white" />
                    </div>
                    <div className="bg-muted px-4 py-3 rounded-2xl rounded-tl-none">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="p-4 border-t border-border">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Ask about universities, scholarships, visa..."
                    className="input-base flex-1 text-sm"
                  />
                  <button onClick={sendMessage} className="btn-primary px-4 py-2 rounded-xl">
                    <Send size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
